import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import FloatingBackButton from './FloatingBackButton';
import type { RevisionSentence, LanguageCode } from '../types/types';
import { useTheme } from './ThemeContext';
import { useSupabaseAudio } from '../hooks/useSupabaseAudio';
import { normalizeText, calculateSimilarity } from '../utils/sentencesgapUtils';
import { getSentenceTranslation } from '../utils/localizedContent';

const MAX_SENTENCES = Infinity;
const MAX_ATTEMPTS = 2;
const SIMILARITY_THRESHOLD = 0.8;
const CLOSE_THRESHOLD = 0.6;

interface GrammarConsolidationProps {
  sentences: RevisionSentence[];
  wordLearned: string;
  wordTranslation: string;
  languageCode: LanguageCode;
  onComplete: () => void;
  onBack: () => void;
  isMobileView?: boolean;
}

const GrammarConsolidation: React.FC<GrammarConsolidationProps> = ({
  sentences,
  wordLearned,
  wordTranslation,
  languageCode,
  onComplete,
  onBack,
  isMobileView = false,
}) => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const { playWord } = useSupabaseAudio(languageCode);

  const selectedSentences = useMemo(() => {
    // Une phrase par testWord (la plus facile), pour couvrir tous les mots appris
    const byWord = new Map<string, RevisionSentence>();
    const sorted = [...sentences].sort((a, b) => (a.difficulty ?? 1) - (b.difficulty ?? 1));
    for (const s of sorted) {
      const key = s.testWord ?? s.original;
      if (!byWord.has(key)) byWord.set(key, s);
    }
    return Array.from(byWord.values());
  }, [sentences]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState<{
    type: 'correct' | 'close' | 'wrong' | 'revealed';
    message: string;
  } | null>(null);
  const [answered, setAnswered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentSentence = selectedSentences[currentIndex];
  const isLastSentence = currentIndex === selectedSentences.length - 1;

  const displayText = useMemo(() => {
    if (!currentSentence?.testWord) return currentSentence?.original ?? '';
    const escaped = currentSentence.testWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return currentSentence.original.replace(new RegExp(escaped, 'i'), '_____');
  }, [currentSentence]);

  const handleSubmit = useCallback(() => {
    if (!currentSentence?.testWord || answered || !userInput.trim()) return;

    const correct = currentSentence.testWord;
    const norm = normalizeText(userInput.trim());
    const normCorrect = normalizeText(correct);
    const similarity = calculateSimilarity(norm, normCorrect);
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);

    if (similarity >= SIMILARITY_THRESHOLD) {
      setFeedback({ type: 'correct', message: t('grammarConsolidation.feedback.correct', { answer: correct }) });
      setAnswered(true);
      if (currentSentence.audio) playWord(currentSentence.audio).catch(() => {});
    } else if (similarity >= CLOSE_THRESHOLD) {
      setFeedback({ type: 'close', message: t('grammarConsolidation.feedback.close', { answer: correct }) });
      setAnswered(true);
    } else if (newAttempts >= MAX_ATTEMPTS) {
      setFeedback({ type: 'revealed', message: t('grammarConsolidation.feedback.revealed', { answer: correct }) });
      setAnswered(true);
    } else {
      setFeedback({ type: 'wrong', message: t('grammarConsolidation.feedback.wrong') });
      setUserInput('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [currentSentence, userInput, attempts, answered, playWord, t]);

  const handleNext = useCallback(() => {
    if (isLastSentence) {
      onComplete();
    } else {
      setCurrentIndex(prev => prev + 1);
      setUserInput('');
      setAttempts(0);
      setFeedback(null);
      setAnswered(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isLastSentence, onComplete]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (answered) handleNext();
      else handleSubmit();
    }
  };

  if (!currentSentence) {
    onComplete();
    return null;
  }

  const isDark = theme === 'dark';

  const feedbackColors = {
    correct:  { bg: 'var(--color-success-tint)', text: 'var(--color-success-light)' },
    close:    { bg: 'var(--color-warning-tint)', text: 'var(--gold-primary)' },
    wrong:    { bg: 'var(--color-error-tint)', text: 'var(--color-error)' },
    revealed: { bg: 'var(--color-error-tint)', text: 'var(--color-error)' },
  };

  return (
    <div className="component-wrapper">
      <div className="grammar-main-container card" data-theme={theme}>

        {/* En-tête */}
        <div className="grammar-header">
          <FloatingBackButton onBack={onBack} mode="inline" variant="ghost" text={t('quiz.back') as string} />
          {!isMobileView && (
            <h2 style={{ color: 'var(--gold-primary)', margin: 0 }}>
              {t('grammarConsolidation.inContext')}<em>{wordLearned}</em>
            </h2>
          )}
        </div>

        <div className="grammar-level-card">

          {/* Progression */}
          <div className="preview-progress">
            <p style={{ margin: '0 0 6px', fontSize: '0.85rem', color: isDark ? '#b8a88c' : '#666' }}>
              {t('grammarConsolidation.sentenceCounter', { current: currentIndex + 1, total: selectedSentences.length })}
            </p>
            <div className="grammar-progress-bar">
              <div
                className="grammar-progress-fill"
                style={{ width: `${((currentIndex + 1) / selectedSentences.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Badge règle */}
          <div style={{
            display: 'inline-block',
            padding: '4px 14px',
            borderRadius: '20px',
            backgroundColor: isDark ? '#2a2621' : '#fdf3dc',
            border: '1px solid var(--gold-primary)',
            marginBottom: '16px',
            fontSize: '0.82rem',
            color: 'var(--gold-primary)',
          }}>
            {wordLearned
            ? <>{t('grammarConsolidation.ruleLabel')}<strong>{answered ? wordLearned : '?'}</strong> = {wordTranslation}</>
            : <>{t('grammarConsolidation.consolidationTitle')}</>
          }
          </div>

          {/* Carte de phrase */}
          <div style={{
            padding: '20px',
            backgroundColor: isDark ? '#1e1a17' : '#fafafa',
            borderRadius: '12px',
            border: `1px solid ${isDark ? '#3a3530' : '#eeeeee'}`,
            marginBottom: '16px',
          }}>
            <p style={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: isDark ? '#e8d5b0' : '#2c1810',
              marginBottom: '8px',
              lineHeight: 1.6,
            }}>
              {displayText}
            </p>
            <p style={{
              fontSize: '0.9rem',
              color: isDark ? '#b8a88c' : '#666',
              fontStyle: 'italic',
              margin: 0,
            }}>
              {getSentenceTranslation(currentSentence, i18n.language)}
            </p>
          </div>

          {/* Saisie */}
          {!answered && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input
                ref={inputRef}
                type="text"
                value={userInput}
                onChange={e => setUserInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('grammarConsolidation.placeholder') as string}
                autoFocus
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: `1.5px solid ${isDark ? '#3a3530' : '#ddd'}`,
                  backgroundColor: isDark ? '#2a2621' : '#fff',
                  color: isDark ? '#e8d5b0' : '#2c1810',
                  fontSize: '1rem',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
              <motion.button
                onClick={handleSubmit}
                className="grammar-action-button primary"
                style={{ flexShrink: 0, padding: '12px 20px' }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={!userInput.trim()}
              >
                {t('exercise.verify')}
              </motion.button>
            </div>
          )}

          {/* Feedback */}
          <AnimatePresence>
            {feedback && (
              <motion.div
                key="feedback"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                style={{
                  padding: '14px 16px',
                  borderRadius: '10px',
                  marginBottom: '16px',
                  backgroundColor: feedbackColors[feedback.type].bg,
                  color: feedbackColors[feedback.type].text,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                }}
              >
                {feedback.type === 'correct' && <CheckCircle size={18} />}
                {(feedback.type === 'close' || feedback.type === 'revealed') && <HelpCircle size={18} />}
                {feedback.type === 'wrong' && <XCircle size={18} />}
                {feedback.message}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Bouton suivant */}
          {answered && (
            <motion.button
              onClick={handleNext}
              className="grammar-action-button primary"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isLastSentence ? t('grammarConsolidation.continue') : t('grammarConsolidation.nextSentence')}
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GrammarConsolidation;
